import { Timestamp } from "google-protobuf/google/protobuf/timestamp_pb";
import { Int32Value } from "google-protobuf/google/protobuf/wrappers_pb";
import {
  OrchestrationCriteria as OrchestrationCriteriaDTO,
  OrchestrationCondition as OrchestrationConditionDTO,
  OccupancyCondition,
  CheckInDayCondition,
  CheckOutDayCondition,
  LengthOfStayCondition,
  BookingWindowCondition,
  BookingWindowConditions,
  TravelWindowCondition,
  TravelWindowConditions,
  NumberOfRoomsCondition,
  DeviceCondition,
  ReferrerCondition,
  UtmSourceCondition,
  OriginCondition,
  SeasonCondition,
  MatchMode as MatchModeDTO,
  DeviceType as DeviceTypeDTO,
  SeasonType as SeasonTypeDTO,
  Hemisphere as HemisphereDTO,
} from "../../../../gen/witbooking/backoffice/orchestrator/common/v1/orchestration_criteria_pb";
import { DayOfWeek as DayOfWeekDTO } from "../../../../gen/witbooking/backoffice/common/common_pb";
import {
  OrchestrationCriteria,
  OrchestrationCondition,
  MatchMode,
  DeviceType,
  DayOfWeek,
  SeasonType,
  Hemisphere,
} from "../domain/orchestrationCriteria.entity";

function timestampToDateString(ts: Timestamp | undefined): string | undefined {
  if (!ts) return undefined;
  const date = new Date(ts.getSeconds() * 1000);
  return date.toISOString().split("T")[0];
}

function dateStringToTimestamp(dateStr: string | undefined): Timestamp | undefined {
  if (!dateStr) return undefined;
  const date = new Date(dateStr);
  const ts = new Timestamp();
  ts.setSeconds(Math.floor(date.getTime() / 1000));
  return ts;
}

const MatchModeToDTOMapping: Record<MatchMode, MatchModeDTO> = {
  [MatchMode.MATCH_MODE_UNSPECIFIED]: MatchModeDTO.MATCH_MODE_UNSPECIFIED,
  [MatchMode.ALL]: MatchModeDTO.ALL,
  [MatchMode.ANY]: MatchModeDTO.ANY,
};

const MatchModeToEntityMapping: Record<MatchModeDTO, MatchMode> = {
  [MatchModeDTO.MATCH_MODE_UNSPECIFIED]: MatchMode.MATCH_MODE_UNSPECIFIED,
  [MatchModeDTO.ALL]: MatchMode.ALL,
  [MatchModeDTO.ANY]: MatchMode.ANY,
};

const DevideTypeToDTOMapping: Record<DeviceType, DeviceTypeDTO> = {
  [DeviceType.DEVICE_TYPE_UNSPECIFIED]: DeviceTypeDTO.DEVICE_TYPE_UNSPECIFIED,
  [DeviceType.DEVICE_TYPE_DESKTOP]: DeviceTypeDTO.DEVICE_TYPE_DESKTOP,
  [DeviceType.DEVICE_TYPE_MOBILE]: DeviceTypeDTO.DEVICE_TYPE_MOBILE,
  [DeviceType.DEVICE_TYPE_TABLET]: DeviceTypeDTO.DEVICE_TYPE_TABLET,
};

const DeviceTypeToEntityMapping: Record<DeviceTypeDTO, DeviceType> = {
  [DeviceTypeDTO.DEVICE_TYPE_UNSPECIFIED]: DeviceType.DEVICE_TYPE_UNSPECIFIED,
  [DeviceTypeDTO.DEVICE_TYPE_DESKTOP]: DeviceType.DEVICE_TYPE_DESKTOP,
  [DeviceTypeDTO.DEVICE_TYPE_MOBILE]: DeviceType.DEVICE_TYPE_MOBILE,
  [DeviceTypeDTO.DEVICE_TYPE_TABLET]: DeviceType.DEVICE_TYPE_TABLET,
};

const DayWeekToDTOMapping: Record<DayOfWeek, DayOfWeekDTO> = {
  [DayOfWeek.MONDAY]: DayOfWeekDTO.MONDAY,
  [DayOfWeek.TUESDAY]: DayOfWeekDTO.TUESDAY,
  [DayOfWeek.WEDNESDAY]: DayOfWeekDTO.WEDNESDAY,
  [DayOfWeek.THURSDAY]: DayOfWeekDTO.THURSDAY,
  [DayOfWeek.FRIDAY]: DayOfWeekDTO.FRIDAY,
  [DayOfWeek.SATURDAY]: DayOfWeekDTO.SATURDAY,
  [DayOfWeek.SUNDAY]: DayOfWeekDTO.SUNDAY,
};

const DayWeekToEntityMapping: Record<DayOfWeekDTO, DayOfWeek> = {
  [DayOfWeekDTO.MONDAY]: DayOfWeek.MONDAY,
  [DayOfWeekDTO.TUESDAY]: DayOfWeek.TUESDAY,
  [DayOfWeekDTO.WEDNESDAY]: DayOfWeek.WEDNESDAY,
  [DayOfWeekDTO.THURSDAY]: DayOfWeek.THURSDAY,
  [DayOfWeekDTO.FRIDAY]: DayOfWeek.FRIDAY,
  [DayOfWeekDTO.SATURDAY]: DayOfWeek.SATURDAY,
  [DayOfWeekDTO.SUNDAY]: DayOfWeek.SUNDAY,
};

const SeasonTypeToDTOMapping: Record<SeasonType, SeasonTypeDTO> = {
  [SeasonType.SEASON_TYPE_UNSPECIFIED]: SeasonTypeDTO.SEASON_TYPE_UNSPECIFIED,
  [SeasonType.SPRING]: SeasonTypeDTO.SPRING,
  [SeasonType.SUMMER]: SeasonTypeDTO.SUMMER,
  [SeasonType.AUTUMN]: SeasonTypeDTO.AUTUMN,
  [SeasonType.WINTER]: SeasonTypeDTO.WINTER,
};

const SeasonTypeToEntityMapping: Record<SeasonTypeDTO, SeasonType> = {
  [SeasonTypeDTO.SEASON_TYPE_UNSPECIFIED]: SeasonType.SEASON_TYPE_UNSPECIFIED,
  [SeasonTypeDTO.SPRING]: SeasonType.SPRING,
  [SeasonTypeDTO.SUMMER]: SeasonType.SUMMER,
  [SeasonTypeDTO.AUTUMN]: SeasonType.AUTUMN,
  [SeasonTypeDTO.WINTER]: SeasonType.WINTER,
};

const HemisphereToDTOMapping: Record<Hemisphere, HemisphereDTO> = {
  [Hemisphere.HEMISPHERE_UNSPECIFIED]: HemisphereDTO.HEMISPHERE_UNSPECIFIED,
  [Hemisphere.NORTHERN]: HemisphereDTO.NORTHERN,
  [Hemisphere.SOUTHERN]: HemisphereDTO.SOUTHERN,
};

const HemisphereToEntityMapping: Record<HemisphereDTO, Hemisphere> = {
  [HemisphereDTO.HEMISPHERE_UNSPECIFIED]: Hemisphere.HEMISPHERE_UNSPECIFIED,
  [HemisphereDTO.NORTHERN]: Hemisphere.NORTHERN,
  [HemisphereDTO.SOUTHERN]: Hemisphere.SOUTHERN,
};

export function conditionToDTO(condition: OrchestrationCondition): OrchestrationConditionDTO {
  const conditionDto = new OrchestrationConditionDTO();

  if (condition.occupancy) {
    const occ = new OccupancyCondition();
    occ.setValuesList(condition.occupancy.values || []);
    conditionDto.setOccupancy(occ);
    return conditionDto;
  }

  if (condition.checkInDay) {
    const cid = new CheckInDayCondition();
    if (condition.checkInDay.activeDays) {
      cid.setActiveDaysList(condition.checkInDay.activeDays.map(d => DayWeekToDTOMapping[d]));
    }
    conditionDto.setCheckInDay(cid);
    return conditionDto;
  }

  if (condition.checkOutDay) {
    const cod = new CheckOutDayCondition();
    if (condition.checkOutDay.activeDays) {
      cod.setActiveDaysList(condition.checkOutDay.activeDays.map(d => DayWeekToDTOMapping[d]));
    }
    conditionDto.setCheckOutDay(cod);
    return conditionDto;
  }

  if (condition.lengthOfStay) {
    const los = new LengthOfStayCondition();
    los.setMin(condition.lengthOfStay.min || 1);
    if (condition.lengthOfStay.max !== undefined) {
      const maxVal = new Int32Value();
      maxVal.setValue(condition.lengthOfStay.max);
      los.setMax(maxVal);
    }
    conditionDto.setLengthOfStay(los);
    return conditionDto;
  }

  if (condition.bookingWindow) {
    const bwc = new BookingWindowConditions();
    condition.bookingWindow.forEach(window => {
      const bw = new BookingWindowCondition();
      if (window.startDate) {
        bw.setStartDate(dateStringToTimestamp(window.startDate));
      }
      if (window.endDate) {
        bw.setEndDate(dateStringToTimestamp(window.endDate));
      }
      if (window.activeDays) {
        bw.setActiveDaysList(window.activeDays.map(d => DayWeekToDTOMapping[d]));
      }
      if (window.startHour) {
        bw.setStartHour(window.startHour);
      }
      if (window.endHour) {
        bw.setEndHour(window.endHour);
      }
      if (window.timeZone) {
        bw.setTimeZone(window.timeZone);
      }
      bwc.addWindows(bw);
    });
    conditionDto.setBookingWindow(bwc);
    return conditionDto;
  }

  if (condition.travelWindow) {
    const twc = new TravelWindowConditions();
    condition.travelWindow.forEach(window => {
      const tw = new TravelWindowCondition();
      if (window.startDate) {
        tw.setStartDate(dateStringToTimestamp(window.startDate));
      }
      if (window.endDate) {
        tw.setEndDate(dateStringToTimestamp(window.endDate));
      }
      if (window.activeDays) {
        tw.setActiveDaysList(window.activeDays.map(d => DayWeekToDTOMapping[d]));
      }
      twc.addWindows(tw);
    });
    conditionDto.setTravelWindow(twc);
    return conditionDto;
  }

  if (condition.numberOfRooms) {
    const nor = new NumberOfRoomsCondition();
    nor.setMin(condition.numberOfRooms.min || 1);
    if (condition.numberOfRooms.max !== undefined) {
      const maxVal = new Int32Value();
      maxVal.setValue(condition.numberOfRooms.max);
      nor.setMax(maxVal);
    }
    conditionDto.setNumberOfRooms(nor);
    return conditionDto;
  }

  if (condition.device) {
    const dev = new DeviceCondition();
    dev.setTypesList((condition.device.types || []).map(t => DevideTypeToDTOMapping[t]));
    conditionDto.setDevice(dev);
    return conditionDto;
  }

  if (condition.referrer) {
    const ref = new ReferrerCondition();
    ref.setValuesList(condition.referrer.values || []);
    conditionDto.setReferrer(ref);
    return conditionDto;
  }

  if (condition.utmSource) {
    const utm = new UtmSourceCondition();
    utm.setValuesList(condition.utmSource.values || []);
    conditionDto.setUtmSource(utm);
    return conditionDto;
  }

  if (condition.origin) {
    const orig = new OriginCondition();
    orig.setValuesList(condition.origin.values || []);
    conditionDto.setOrigin(orig);
    return conditionDto;
  }

  if (condition.season) {
    const season = new SeasonCondition();
    season.setSeasonsList((condition.season.seasons || []).map(s => SeasonTypeToDTOMapping[s]));
    season.setHemisphere(HemisphereToDTOMapping[condition.season.hemisphere]);
    conditionDto.setSeason(season);
    return conditionDto;
  }

  return conditionDto;
}

export function dtoToCondition(conditionDto: OrchestrationConditionDTO): OrchestrationCondition {
  const obj = conditionDto.toObject();
  const condition: OrchestrationCondition = {};

  if (obj.occupancy) {
    condition.occupancy = { values: obj.occupancy.valuesList };
    return condition;
  }

  if (obj.checkInDay) {
    condition.checkInDay = {
      activeDays: obj.checkInDay.activeDaysList.map(d => DayWeekToEntityMapping[d]),
    };
    return condition;
  }

  if (obj.checkOutDay) {
    condition.checkOutDay = {
      activeDays: obj.checkOutDay.activeDaysList.map(d => DayWeekToEntityMapping[d]),
    };
    return condition;
  }

  if (obj.lengthOfStay) {
    condition.lengthOfStay = {
      min: obj.lengthOfStay.min,
      max: obj.lengthOfStay.max?.value,
    };
    return condition;
  }

  if (obj.bookingWindow) {
    const windowsList = conditionDto.getBookingWindow()?.getWindowsList() || [];
    condition.bookingWindow = windowsList.map(window => ({
      startDate: window.getStartDate() ? timestampToDateString(window.getStartDate()) : undefined,
      endDate: window.getEndDate() ? timestampToDateString(window.getEndDate()) : undefined,
      activeDays: window.getActiveDaysList()?.map(d => DayWeekToEntityMapping[d]),
      startHour: window.getStartHour() || undefined,
      endHour: window.getEndHour() || undefined,
      timeZone: window.getTimeZone() || undefined,
    }));
    return condition;
  }

  if (obj.travelWindow) {
    const windowsList = conditionDto.getTravelWindow()?.getWindowsList() || [];
    condition.travelWindow = windowsList.map(window => ({
      startDate: window.getStartDate() ? timestampToDateString(window.getStartDate()) : undefined,
      endDate: window.getEndDate() ? timestampToDateString(window.getEndDate()) : undefined,
      activeDays: window.getActiveDaysList()?.map(d => DayWeekToEntityMapping[d]),
    }));
    return condition;
  }

  if (obj.numberOfRooms) {
    condition.numberOfRooms = {
      min: obj.numberOfRooms.min,
      max: obj.numberOfRooms.max?.value,
    };
    return condition;
  }

  if (obj.device) {
    condition.device = {
      types: obj.device.typesList.map(t => DeviceTypeToEntityMapping[t]),
    };
    return condition;
  }

  if (obj.referrer) {
    condition.referrer = { values: obj.referrer.valuesList };
    return condition;
  }

  if (obj.utmSource) {
    condition.utmSource = { values: obj.utmSource.valuesList };
    return condition;
  }

  if (obj.origin) {
    condition.origin = { values: obj.origin.valuesList };
    return condition;
  }

  if (obj.season) {
    condition.season = {
      seasons: obj.season.seasonsList.map(s => SeasonTypeToEntityMapping[s]),
      hemisphere: HemisphereToEntityMapping[obj.season.hemisphere],
    };
    return condition;
  }

  return condition;
}

export function criteriaToDTO(criteria: OrchestrationCriteria): OrchestrationCriteriaDTO {
  const criteriaDto = new OrchestrationCriteriaDTO();
  criteriaDto.setMatchMode(MatchModeToDTOMapping[criteria.matchMode]);
  criteriaDto.setConditionsList(criteria.conditions.map(conditionToDTO));
  return criteriaDto;
}

export function dtoToCriteria(criteriaDto: OrchestrationCriteriaDTO): OrchestrationCriteria {
  return {
    matchMode: MatchModeToEntityMapping[criteriaDto.getMatchMode()],
    conditions: criteriaDto.getConditionsList().map(dtoToCondition),
  };
}
